import React, { useState, useEffect } from 'react';
import dashboardService from '../../services/dashboard';
import newsService, { NewsArticle, NewsResponse } from '../../services/newsService';
import NewsCard from '../../components/NewsCard';

interface DashboardStats {
  totalPatients: number;
  todayAppointmentsTotal: number;
  todayAppointmentsCompleted: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    todayAppointmentsTotal: 0,
    todayAppointmentsCompleted: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalNews, setTotalNews] = useState<number>(0);
  const pageSize = 10;

  useEffect(() => {
    loadDashboardData();
    loadNews(1);
  }, []);

  const loadNews = async (page: number): Promise<void> => {
    try {
      setNewsLoading(true);
      const response = await newsService.fetchNews(page, pageSize);
      setNews(response.articles);
      setCurrentPage(response.page);
      setTotalPages(response.totalPages);
      setTotalNews(response.total);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
      setNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const loadDashboardData = async (): Promise<void> => {
    try {
      const [patientsCount, todayStats] = await Promise.all([
        dashboardService.getActivePatientsCount(),
        dashboardService.getTodayAppointmentsStats(),
      ]);

      setStats({
        totalPatients: patientsCount,
        todayAppointmentsTotal: todayStats.total,
        todayAppointmentsCompleted: todayStats.completed,
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setStats({
        totalPatients: 0,
        todayAppointmentsTotal: 0,
        todayAppointmentsCompleted: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Content Header (Page header) */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">Página Inicial</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-3 col-6">
              <div className="small-box bg-info">
                <div className="inner">
                  <h3>{loading ? '-' : stats.totalPatients}</h3>
                  <p>Pacientes</p>
                </div>
                <div className="icon">
                  <i className="fas fa-user"></i>
                </div>
                <a href="/patients" className="small-box-footer">
                  Mais informações <i className="fas fa-arrow-circle-right"></i>
                </a>
              </div>
            </div>
            
            <div className="col-lg-3 col-6">
              <div className="small-box bg-success">
                <div className="inner">
                  <h3>
                    {loading ? '-' : `${stats.todayAppointmentsCompleted}/${stats.todayAppointmentsTotal}`}
                  </h3>
                  <p>Atendimentos Hoje (Concluídos/Total)</p>
                </div>
                <div className="icon">
                  <i className="fas fa-calendar-check"></i>
                </div>
                <a href="/appointments" className="small-box-footer">
                  Mais informações <i className="fas fa-arrow-circle-right"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Seção de Notícias de Psicologia */}
          <div className="row mt-4">
            <div className="col-md-12">
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-newspaper mr-2"></i>
                    Notícias sobre Psicologia
                  </h3>
                  <div className="card-tools">
                    <button 
                      type="button" 
                      className="btn btn-tool" 
                      onClick={() => loadNews(currentPage)}
                      disabled={newsLoading}
                    >
                      <i className={`fas fa-sync-alt ${newsLoading ? 'fa-spin' : ''}`}></i>
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  {newsLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Carregando...</span>
                      </div>
                      <p className="mt-2 text-muted">Carregando notícias...</p>
                    </div>
                  ) : news.length > 0 ? (
                    <>
                      <div className="row">
                        {news.map((article, index) => (
                          <div key={index} className="col-lg-6 col-md-12 mb-4">
                            <NewsCard 
                              article={article} 
                              dateFormatter={newsService.formatDate}
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Paginação */}
                      {totalPages > 1 && (
                        <div className="row mt-3">
                          <div className="col-12">
                            <nav aria-label="Navegação de notícias">
                              <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => loadNews(currentPage - 1)}
                                    disabled={currentPage === 1 || newsLoading}
                                  >
                                    <i className="fas fa-chevron-left"></i> Anterior
                                  </button>
                                </li>
                                
                                {/* Páginas */}
                                {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                                  let pageNum: number;
                                  if (totalPages <= 5) {
                                    pageNum = idx + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = idx + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + idx;
                                  } else {
                                    pageNum = currentPage - 2 + idx;
                                  }
                                  
                                  return (
                                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                      <button 
                                        className="page-link" 
                                        onClick={() => loadNews(pageNum)}
                                        disabled={newsLoading}
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                })}
                                
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => loadNews(currentPage + 1)}
                                    disabled={currentPage === totalPages || newsLoading}
                                  >
                                    Próxima <i className="fas fa-chevron-right"></i>
                                  </button>
                                </li>
                              </ul>
                            </nav>
                            <p className="text-center text-muted">
                              <small>
                                Página {currentPage} de {totalPages} ({totalNews} notícias no total)
                              </small>
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-exclamation-circle fa-3x text-muted mb-3"></i>
                      <p className="text-muted">Não foi possível carregar as notícias no momento.</p>
                      <button 
                        className="btn btn-primary btn-sm" 
                        onClick={() => loadNews(1)}
                      >
                        <i className="fas fa-redo mr-2"></i>
                        Tentar novamente
                      </button>
                    </div>
                  )}
                </div>
                {!newsLoading && news.length > 0 && (
                  <div className="card-footer text-center">
                    <small className="text-muted">
                      <i className="fas fa-info-circle mr-1"></i>
                      Notícias do Google News sobre psicologia, saúde mental e terapia
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Dashboard;